import { inject } from 'aurelia';
import { ProjectModelService, ProjectDraftData } from './project-model-service';

interface ProjectDraftEnvelope {
    schemaVersion: number;
    savedAt: number;
    draft: ProjectDraftData;
}

@inject(ProjectModelService)
export class ProjectDraftStorageService {
    private static readonly _key = 'sammandrag_project_draft_v1';
    private static readonly _schemaVersion = 1;
    private static readonly _maxAgeMs = 7 * 24 * 60 * 60 * 1000;

    constructor(private readonly _projectModelService: ProjectModelService) {}

    public saveDraft(): void {
        const draft = this._projectModelService.exportDraft();
        if (draft == null) {
            return;
        }

        const envelope: ProjectDraftEnvelope = {
            schemaVersion: ProjectDraftStorageService._schemaVersion,
            savedAt: Date.now(),
            draft,
        };

        try {
            localStorage.setItem(ProjectDraftStorageService._key, JSON.stringify(envelope));
        } catch {
            // Ignore storage failures (private mode/quota), app can continue without draft persistence.
        }
    }

    public tryRestoreDraft(): string | null {
        let envelope: ProjectDraftEnvelope | null = null;

        try {
            const raw = localStorage.getItem(ProjectDraftStorageService._key);
            if (!raw) {
                return null;
            }

            envelope = JSON.parse(raw) as ProjectDraftEnvelope;
        } catch {
            this.clearDraft();
            return null;
        }

        if (!this._isValidEnvelope(envelope)) {
            this.clearDraft();
            return null;
        }

        try {
            const eventId = this._projectModelService.importDraft(envelope.draft);
            this.clearDraft();
            return eventId;
        } catch {
            // Catch runtime shape mismatches after deploy/class changes.
            this.clearDraft();
            return null;
        }
    }

    public clearDraft(): void {
        try {
            localStorage.removeItem(ProjectDraftStorageService._key);
        } catch {
            // Ignore storage failures.
        }
    }

    private _isValidEnvelope(envelope: ProjectDraftEnvelope | null): envelope is ProjectDraftEnvelope {
        if (envelope == null) {
            return false;
        }

        if (envelope.schemaVersion !== ProjectDraftStorageService._schemaVersion) {
            return false;
        }

        if (!Number.isFinite(envelope.savedAt)) {
            return false;
        }

        if (Date.now() - envelope.savedAt > ProjectDraftStorageService._maxAgeMs) {
            return false;
        }

        return envelope.draft != null;
    }
}
